import {
	FunctionComponent,
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { BusyIndicator } from "client/Components";
import {
	MediaQueryKind,
	useForceRerender,
	useSynchronizedRef,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { tryParseBoolean } from "client/Misc/parseUtils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { ExchangeEmailId } from "client/Api/Email/common";
import {
	EmailScreenModel,
	ExchangeAccountModel,
	getEmailScreenModel,
	IFolderViewModel,
	SignatureModel,
	ExchangeConfigurationErrorKind,
	ExchangeConfigurationErrorResponseModel,
	LazyLoadedChildren
} from "client/Api/Email/email";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { EmailNotConfiguredDialog } from "./Dialogs/EmailNotConfiguredDialog";
import {
	EmailPage,
	EmailPageHandle,
	EmailResponseKind,
	EmailResponseSessionState,
	EmailSessionState,
	EmailSessionStateKind
} from "./EmailPage/EmailPage";
import styles from "./Email.module.less";
import { AppType } from "client/Api/Apps/common";
import { usePageTitle } from "client/Hooks/usePageTitle";
import { localize } from "client_legacy/ndaw/culture";
import {
	DraftPageHistoryState,
	EmailHistoryStateKind,
	isDraftPageHistoryState
} from "./emailHistoryStates";
import { never } from "client_legacy/ndaw/errors";
import {
	EmailUrlState,
	EmailUrlStateKind,
	useEmailUrlState
} from "./NavigationHooks/useEmailUrlState";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { useSignatureStore } from "./Signatures/signatureStore";
import {
	useGlobalNavigationStore,
	GlobalNavigationCriteria
} from "../Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	connectToRouter,
	route
} from "client/Navigation";
import { ExchangeAuthenticationErrorDialog } from "./Dialogs/ExchangeAuthenticationErrorDialog";
import { HttpRequestError } from "client/Api/httpClient";
import { PageFailedDialog } from "./Dialogs/PageFailedDialog";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { useAnalyticsStore } from "../Analytics/analyticsStore";
import { TitleBarBackButton } from "client/Features/Layout/TitleBarBackButton/TitleBarBackButton";
import {
	convertToExchangeAccountItem,
	ExchangeAccountItem,
	fetchFolders,
	FolderBranch,
	tryToFindFolder
} from "./common";

export type EmailProps = {
	historyState: unknown;
	onNavigate: NavigationCallback;
	emailToOpen?: string;
	to?: string;
	cc?: string;
	bcc?: string;
	createNewEmail?: boolean;
	draftIdToEdit?: string;
	folderToOpen?: string;
	mailboxToOpen?: string;
};

export const Email = (
	props: EmailProps
): ReactElement => {
	usePageTitle(localize("_Email"));

	const {
		historyState,
		onNavigate,
		emailToOpen,
		to,
		cc,
		bcc,
		createNewEmail,
		draftIdToEdit,
		folderToOpen,
		mailboxToOpen
	} = props;

	const urlState: EmailUrlState = useEmailUrlState({
		emailToOpen,
		to,
		cc,
		bcc,
		createNewEmail,
		draftIdToEdit,
		folderToOpen,
		mailboxToOpen
	});

	const [screenModel, setScreenModel] = useState<EmailScreenModel | null>(null);
	const [accountItems, setAccountItems] = useState<readonly ExchangeAccountItem[]>(EMPTY_ARRAY);
	const [isBusy, setIsBusy] = useState<boolean>(true);

	const [selectedFolderWithMailbox, setSelectedFolderWithMailbox] = useState<SelectedFolderWithMailbox>();
	const [errorState, setErrorState] = useState<ErrorState>({
		hasError: false
	});

	const [onReload, modelVersion] = useForceRerender();
	const [explicitSessionIdVersion, setExplicitSessionIdVersion] = useState<Guid>((): Guid => newGuid());
	const emailPageRef: RefObject<EmailPageHandle | null> = useRef<EmailPageHandle>(null);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const setSignatures: (signatures: readonly SignatureModel[]) => void = useSignatureStore(x => x.setSignatures);
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const setActiveGlobalNavigationCriteria: (criteria: GlobalNavigationCriteria) => () => void = useGlobalNavigationStore(
		x => x.setActiveCriteria
	);

	const sessionId = useMemo(
		(): Guid => {
			hasImplicitDependencyOn(urlState);
			hasImplicitDependencyOn(explicitSessionIdVersion);

			return newGuid();
		},
		[urlState, explicitSessionIdVersion]
	);

	const sessionState = useMemo(
		(): EmailSessionState => {
			switch (urlState.kind) {
				case EmailUrlStateKind.Compose:
					return {
						kind: EmailSessionStateKind.NewEmail,
						to: urlState.to,
						cc: urlState.cc,
						bcc: urlState.bcc,
						sessionId
					};
				case EmailUrlStateKind.OpenEmail:
					return {
						kind: EmailSessionStateKind.ViewEmail,
						emailId: {
							id: urlState.emailId,
							hasValue: true
						},
						smtpAddress: urlState.smtpAddress
					};
				case EmailUrlStateKind.EditDraft:
					let response: EmailResponseSessionState | undefined = undefined;

					if (isDraftPageHistoryState(historyState) && isNotNil(historyState.response)) {
						const {
							originalEmailId,
							kind
						} = historyState.response;

						response = {
							originalEmailId,
							kind
						};
					}

					return {
						kind: EmailSessionStateKind.EditDraft,
						emailId: {
							id: urlState.draftId,
							hasValue: true
						},
						smtpAddress: urlState.smtpAddress,
						doesRepresentNewlyGeneratedDraft: isNotNil(response),
						response,
						sessionId
					};
				case EmailUrlStateKind.OpenFolder:
				case EmailUrlStateKind.Default:
					return {
						kind: EmailSessionStateKind.None
					};
				default:
					never(urlState);
			}
		},
		[
			historyState,
			urlState,
			sessionId
		]
	);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(modelVersion);

			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setErrorState({
						hasError: false
					});

					setIsBusy(true);
					setScreenModel(null);
					setAccountItems(EMPTY_ARRAY);

					const model: EmailScreenModel = await getEmailScreenModel(signal);

					setTemporaryIdsOnFaultedAccounts(model);

					setSignatures(
						[
							model.signatures.personal,
							model.signatures.managed
						]
							.filter(isNotNil)
					);

					setScreenModel(model);
					setAccountItems(
						model
							.exchangeAccounts
							.map(convertToExchangeAccountItem)
					);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					let error: ExchangeConfigurationErrorKind | undefined;
					if (e instanceof HttpRequestError && e.statusCode === 400) {
						try {
							const {
								errorKind
							} = e.readJson<ExchangeConfigurationErrorResponseModel>();

							switch(errorKind) {
								case ExchangeConfigurationErrorKind.ExchangeIsNotConfigured:
								case ExchangeConfigurationErrorKind.AuthenticationError:
									error = errorKind;
									break;
								default:
									console.error(`Unsupported error reason "${errorKind}"`);
									break;
							}
						} catch (e) {
							console.error(e);
						}
					}

					setErrorState({
						hasError: true,
						errorKind: error
					});

					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[modelVersion, setSignatures]
	);

	useEffect(
		(): (() => void) => {
			addAnalyticsAppEvent({
				appType: AppType.Email
			});

			return setActiveGlobalNavigationCriteria({
				type: "app",
				appType: AppType.Email
			});
		},
		[setActiveGlobalNavigationCriteria, addAnalyticsAppEvent]
	);

	const accountItemsRef: ReadonlyRefObject<readonly ExchangeAccountItem[]> = useSynchronizedRef(accountItems);

	const fetchFoldersHandler = useCallback(
		async (
			smtpAddress: string,
			folderId: string,
			shouldExcludeSelf: boolean
		): Promise<readonly ExchangeAccountItem[]> => {
			let updatedAccountItems: readonly ExchangeAccountItem[] = accountItemsRef.current;

			const result: AsyncIterable<readonly ExchangeAccountItem[]> = fetchFolders(
				accountItemsRef.current,
				smtpAddress,
				folderId,
				shouldExcludeSelf,
				globalCancellation
			);

			for await (const accounts of result) {
				setAccountItems(accounts);
				updatedAccountItems = accounts;
			}

			return updatedAccountItems;
		},
		[
			accountItemsRef,
			globalCancellation
		]
	);

	const findAndSelectLocation = useCallback(
		async (
			smtpAddress: string | nil,
			folderId: string | nil
		): Promise<void> => {
			if (isNil(screenModel)) {
				return;
			}

			const defaultAccount: ExchangeAccountModel = ensureNotNil(
				screenModel
					.exchangeAccounts
					.find(
						x => !x.isFaulted
							&& x.folders.length > 0
					),
				"There is no exchange account to select"
			);

			const defaultFolder: IFolderViewModel<LazyLoadedChildren> = ensureNotNil(
				getDefaultFolder(defaultAccount),
				"There is no folder to select"
			);

			let account: ExchangeAccountModel | undefined;
			let folder: IFolderViewModel<LazyLoadedChildren> | undefined;

			if (isNotNil(smtpAddress)) {
				account = screenModel
					.exchangeAccounts
					.find(x => x.emailAddress === smtpAddress);

				if (isNil(account)) {
					console.error(`Account with id ${smtpAddress} was not found.`);

					account = defaultAccount;
					folder = defaultFolder;
				}
			}

			if (isNil(account)) {
				account = defaultAccount;
			}

			const mailboxId: string = account
				.emailAddress;

			if (isNotNil(folderId) && isNil(folder)) {
				const updatedAccountItems: readonly ExchangeAccountItem[] = await fetchFoldersHandler(
					mailboxId,
					folderId,
					true
				);

				const folderResult: TryResult<FolderBranch> = tryToFindFolder(
					updatedAccountItems,
					mailboxId,
					folderId
				);

				if (folderResult.wasSuccessful) {
					folder = folderResult
						.result
						.folder;
				} else {
					console.error(`Could not find folder with id ${folderId}`);
				}
			}

			if (isNil(folder)) {
				folder = ensureNotNil(
					getDefaultFolder(account),
					"There is no folder to select"
				);
			}

			setSelectedFolderWithMailbox({
				account,
				folder
			});
		},
		[
			screenModel,
			fetchFoldersHandler
		]
	);

	const hasSelectedFolder: boolean = isNotNil(selectedFolderWithMailbox);

	const hasSelectedFolderRef: ReadonlyRefObject<boolean> = useSynchronizedRef(hasSelectedFolder);

	useEffect(
		(): void => {
			let mailBoxId: string | nil;
			let folderId: string | nil;

			switch (urlState.kind) {
				case EmailUrlStateKind.OpenEmail:
					return;
				case EmailUrlStateKind.Compose:
				case EmailUrlStateKind.Default:
				case EmailUrlStateKind.EditDraft:
					if (hasSelectedFolderRef.current) {
						return;
					}
					break;
				case EmailUrlStateKind.OpenFolder:
					mailBoxId = urlState.smtpAddress;
					folderId = urlState.folderId;
					break;
				default:
					never(urlState);
			}

			findAndSelectLocation(
				mailBoxId,
				folderId
			);
		},
		[
			urlState,
			globalCancellation,
			hasSelectedFolderRef,
			findAndSelectLocation
		]
	);

	const switchToNoSessionHandler = useCallback(
		(): void => {
			onNavigate(emailRouting.page({ queryParams: {} }));
		},
		[onNavigate]
	);

	const composeNewEmailHandler = useCallback(
		(): void => {
			setExplicitSessionIdVersion(newGuid());
			onNavigate(
				emailRouting.page({
					queryParams: {
						createNewEmail: "true"
					}
				})
			);
		},
		[onNavigate]
	);

	const openResponseDraftHandler = useCallback(
		(
			draftId: string,
			responseKind: EmailResponseKind,
			originalEmailId: ExchangeEmailId,
			smtpAddress: string | null
		): void => {
			const newState: DraftPageHistoryState = {
				kind: EmailHistoryStateKind.DraftPage,
				response: {
					kind: responseKind,
					originalEmailId
				}
			};

			onNavigate(
				emailRouting.page({
					queryParams: {
						draftIdToEdit: draftId,
						mailboxToOpen: smtpAddress ?? undefined
					}
				}),
				{ historyState: newState }
			);
		},
		[onNavigate]
	);

	const openEmailHandler = useCallback(
		(emailId: string, isForEditing: boolean): void => {
			onNavigate(
				emailRouting.page({
					queryParams: isForEditing
						? {
							draftIdToEdit: emailId,
							mailboxToOpen: selectedFolderWithMailbox
								?.account
								.emailAddress
						}
						: {
							emailToOpen: emailId,
							mailboxToOpen: selectedFolderWithMailbox
								?.account
								.emailAddress
						}
				})
			);
		},
		[
			selectedFolderWithMailbox?.account.emailAddress,
			onNavigate
		]
	);

	const openFolderHandler = useCallback(
		(smtpAddress: string | null, folderId: string): void => {
			findAndSelectLocation(
				smtpAddress,
				folderId
			);
		},
		[findAndSelectLocation]
	);

	const navigateToFolderHandler = useCallback(
		(smtpAddress: string, folderId: string): void => {
			onNavigate(
				emailRouting.page({
					queryParams: {
						folderToOpen: folderId,
						mailboxToOpen: smtpAddress
					}
				})
			);
		},
		[onNavigate]
	);

	const renderErrorDialogs = (): ReactElement | null => {
		if (errorState.hasError === false) {
			return null;
		}

		switch(errorState.errorKind) {
			case ExchangeConfigurationErrorKind.AuthenticationError:
				return <ExchangeAuthenticationErrorDialog />;
			case ExchangeConfigurationErrorKind.ExchangeIsNotConfigured:
				return (
					<EmailNotConfiguredDialog
						shouldNavigateBackToWorkspaceOnClose
					/>
				);
			default:
				return <PageFailedDialog />;
		}
	};

	return (
		<>
			<BackButton
				isMobile={isMobile}
				sessionStateKind={sessionState.kind}
				onBack={switchToNoSessionHandler}
			/>
			<div id="main-content">
				<div className={styles.root}>
					{isBusy && <BusyIndicator />}
					{!isBusy && isNotNil(screenModel) && isNotNil(accountItems) && (
						<EmailPage
							screenModel={screenModel}
							accountItems={accountItems}
							sessionState={sessionState}
							selectedFolderWithMailbox={selectedFolderWithMailbox}
							onReloadScreenModel={onReload}
							onSwitchToNoSession={switchToNoSessionHandler}
							onComposeNewEmail={composeNewEmailHandler}
							onOpenResponseDraft={openResponseDraftHandler}
							onOpenEmail={openEmailHandler}
							onOpenFolder={openFolderHandler}
							onNavigateToFolder={navigateToFolderHandler}
							ref={emailPageRef}
							onAccountsChange={setAccountItems}
							onFetchFolders={fetchFoldersHandler}
						/>
					)}
					{renderErrorDialogs()}
				</div>
			</div>
		</>
	);
};
Email.displayName="Email";

type BackButtonProps = {
	isMobile: boolean;
	sessionStateKind: EmailSessionStateKind;
	onBack(): void;
};

const BackButton = (props: BackButtonProps): ReactElement | null => {
	const {
		isMobile,
		sessionStateKind,
		onBack
	} = props;

	if (!isMobile) {
		return null;
	}

	switch (sessionStateKind) {
		case EmailSessionStateKind.NewEmail:
		case EmailSessionStateKind.EditDraft:
		case EmailSessionStateKind.ViewEmail:
			return (
				<TitleBarBackButton
					onBack={onBack}
				/>
			);
		case EmailSessionStateKind.None:
			return null;
		default:
			never(sessionStateKind);
	}
};

type ErrorState = {
	hasError: false;
} | {
	hasError: true;
	errorKind?: ExchangeConfigurationErrorKind;
};

export type SelectedFolderWithMailbox = {
	account: ExchangeAccountModel;
	folder: IFolderViewModel<LazyLoadedChildren>;
};

function getDefaultFolder(account: ExchangeAccountModel): IFolderViewModel<LazyLoadedChildren> | undefined {
	return account
		.folders
		.find(f => f.isInbox)
			?? account.folders[0];
}

export function setTemporaryIdsOnFaultedAccounts(model: EmailScreenModel): void {
	for (const account of model.exchangeAccounts) {
		if (account.isFaulted) {
			account.rootFolderId = {
				id: newGuid()
			};
		}
	}
}

export const EmailForRouter: FunctionComponent = connectToRouter(
	Email,
	{
		"historyState": PredefinedPropOptionKind.HistoryState,
		"onNavigate": PredefinedPropOptionKind.NavigateCallback,
		"emailToOpen": {
			isRequired: false,
			type: "query"
		},
		"to": {
			isRequired: false,
			type: "query"
		},
		"cc": {
			isRequired: false,
			type: "query"
		},
		"bcc": {
			isRequired: false,
			type: "query"
		},
		"createNewEmail": {
			isRequired: false,
			type: "query",
			tryParse: tryParseBoolean
		},
		"draftIdToEdit": {
			isRequired: false,
			type: "query"
		},
		"folderToOpen": {
			isRequired: false,
			type: "query"
		},
		"mailboxToOpen": {
			isRequired: false,
			type: "query"
		}
	}
);

export type EmailQueryParameters = QueryParametersFromProps<
	EmailProps,
	| "emailToOpen"
	| "to"
	| "cc"
	| "bcc"
	| "createNewEmail"
	| "draftIdToEdit"
	| "folderToOpen"
	| "mailboxToOpen"
>;

export const emailRouting = {
	page: route<undefined, EmailQueryParameters>`/Email`
};